<template>
  <div v-if="lang.id == post.lang_id" class="post" :class="primaryPostClass">
    <b-modal v-model="showNetworkSplit" type="warn" :title="`Confirm Split: ${networkName}`" @ok="splitSocialNetwork()" ok-title="Yes">
			<p>Do you want to split {{networkName}} into its own post?</p>
		</b-modal>
    <b-modal v-model="showNetworkReturn" type="warn" :title="`Confirm Return: ${networkName}`" @ok="returnSocialNetwork()" ok-title="Yes">
			<p>Do you want to merge {{networkName}} with the primary post?</p>
      <p><strong>NOTE:</strong> All content and comments specific to this post will be lost.</p>
		</b-modal>
    <b-modal v-model="showNetworkSplitErr" title="Network could not be split." ok-only>
			<p>Primary post must have more than one social network to split.</p>
		</b-modal>
		<div class="language-heading-wrapper">
 	  <h3 class="language-heading">{{lang.lang_name}}</h3>
    <div class="platforms">
      <div class="platform" v-for="network in post.social_networks">
        <h4 class="platform-heading">
          <span class="network-button" :data-network-id="network" @click="networkButtonClick">
            <i :class="`fab fa-${getSocialNetworkClassFromId(network)} platform-indicator`" :title="getSocialNetworkNameFromId(network)" ></i>
          </span>
        </h4>
        <span class="remaining-words" :class=getRemainingWordClass(network)>{{ getRemainingWordCount(network) }}</span>
      </div>
    </div>
    </div>
    <div class="post-editor-fields-wrapper">
  <fieldset>
      <label class="field-label-with-content-icon" :for="`caption-${post.id}`">Post Copy <i v-b-tooltip.hover
  			title="No Post Copy or Share/Retweet" v-show="!hasContent" class="fas fa-exclamation-circle missing-content-icon"></i>
      </label>
      <div class="caption-wrap">
        <textarea class="post-caption" :class="{ 'min-height-set': caption }" 
        v-model="caption" @blur="postCaptionUpdated"
        :id="`post-caption-${post.id}`"></textarea>
        <emoji-picker @emoji="insertEmoji" :search="emojiSearch" class="emoji-picker-outer">
          <button class="emoji-invoker" slot="emoji-invoker" slot-scope="{ events: { click: clickEvent } }" @click.stop="clickEvent">
            <i class="far fa-smile"></i>
          </button>
          <div class="emoji-picker" slot="emoji-picker" slot-scope="{ emojis, insert, display }">
            <div class="emoji-search-wrap">
              <input type="text" placeholder="Search Emoji" v-model="emojiSearch">
            </div>
            <div class="emoji-list-wrap">
              <div class="emoji-category" v-for="(emojiGroup, category) in emojis" :key="category">
                <h5>{{ category }}</h5>
                <div class="emoji-category-content">
                  <span
                    v-for="(emoji, emojiName) in emojiGroup"
                    :key="emojiName"
                    @click="insertEmoji(emoji)"
                    :title="emojiName"
                  >{{ emoji }}</span>
                </div>
              </div>
            </div>
          </div>
        </emoji-picker>
        <fieldset 
          v-if="getSystemSetting('url_shortener_service') && post.caption && post.caption != ''"
          :id="`fs-url-${post.id}`">
          <button 
            type="button" class="btn btn-theme" 
            :id="`btn-shorten-${post.id}`"
            @click="shortenURL(post.id)">Shorten URL</button>
          <button 
            type="button" class="btn btn-theme" 
            :id="`btn-lengthen-${post.id}`"
            @click="lengthenURL(post.id)">Long URL</button>
        </fieldset>
      </div>
    </fieldset>

    <SocialPostImageSlider :socialPost="post" location="post-editor" :isEditor="true" :namespace="namespace"/>

    <fieldset :class="getFileClass">
      <VueFilePond
          name="file"
          ref="resourcePond"
          :label-idle="getIdleLabel"
          :allow-multiple="true"
          :dropOnElement="true"
          :dropOnPage="false"
          :allowRevert="false"
          v-bind:files="filepond"
          v-on:init="handleFilePondInit"
          credits="false"
        />
      <draggable
        v-model="post.files"
        :disabled="imageDraggingDisabled"
        :options="{forceFallback: true}"
        class="post-image-group"
        ghost-class="ghost"
        @change="imageOrderChanged"
        @start="dragStart"
        @update="dragUpdate"
        @end="dragEnd"
      >
        
        <SocialPostImageDraggable v-for="image in post.files" 
          :image="image" 
          :key="`image-draggable-${image.id}`" 
          :dragging="dragging"
          :disabled="imageDraggingDisabled"
          :namespace="namespace"
        />
      </draggable>
    </fieldset>
    

    <!-- 
    <fieldset>
      <label :for="`url-${post.id}`">URL</label>
      <input :class="getClasses" type="url" :name="`url-${post.id}`" :id="`url-${post.id}`" v-model="url" @blur="postUrlUpdated" @keyup.enter="postUrlUpdated"  />
      <div class="error" v-if="!isValid">URL is invalid</div>
    </fieldset>
    <fieldset>
      <label :for="`short-url-${post.id}`">Shortened URL</label>
      <input :class="getClassesShortUrl" type="url" :name="`short-url-${post.id}`" :id="`short-url-${post.id}`" v-model="short_url" @blur="postShortUrlUpdated" @keyup.enter="postShortUrlUpdated"  />
      <div class="error" v-if="!isValidShortUrl">Short URL is invalid</div>
    </fieldset>
     -->
    <div class="share-url-group">
      <b-form-checkbox v-model="showShareUrl" @change="showShareUrlUpdated" :disabled="showShareUrlDisabled">
        Share/Retweet URL
      </b-form-checkbox>
      <b-collapse v-model="showShareUrl">
        <SocialPostShareUrl v-for="shareUrl in post.share_urls" 
        :post="post" 
        :shareUrl="shareUrl"
        :network="getSocialNetworkFromId(shareUrl.id)" 
        :key="'share-url-'+post.id+'-network-'+shareUrl.id"
        @updated="socialPostShareUrlUpdated"
      />
      </b-collapse>
    </div>
  </div>
  </div>
</template>

<script>
import vueFilePond from "vue-filepond";
const VueFilePond = vueFilePond();
import "filepond/dist/filepond.min.css";
import {mapMutations, mapGetters, mapState} from 'vuex';
import draggable from 'vuedraggable';
import SocialPostImageDraggable from "./SocialPostImageDraggable.vue";
import SocialPostImageSlider from "./SocialPostImageSlider.vue";
import Autolinker from 'autolinker';
import EmojiPicker from 'vue-emoji-picker';

import SocialPostWordCount from "./SocialPostWordCount.vue";
import SocialPostShareUrl from "./SocialPostShareUrl.vue";

export default {
  data() {
    return {
    	hasContent:false,
      filepond: [],
      caption: "",
      url: "",
      short_url: "",
      post_images: [],
      imageDraggingDisabled: false,
      showNetworkSplit: false,
      showNetworkSplitErr: false,
      showNetworkReturn: false,
      networkId: false,
      networkName: "",
      updatingNetwork: false,
      dragging: false,
      // isValid: true,
      // isValidShortUrl: true,
      showShareUrl: false,
      showShareUrlDisabled: false,
      emojiInput: "",
      emojiSearch: "",
    }
  },
  components: {
    VueFilePond,
    draggable,
    SocialPostImageDraggable,
    SocialPostImageSlider,
    SocialPostWordCount,
    SocialPostShareUrl,
    EmojiPicker,
  },
  props: ['lang', 'post', 'namespace'],
  computed: {
	  	...mapState({
		    state (state) {
		      return state[this.namespace]
		    },
		    getSocialNetworkClass(state, getters) {
		      return getters['getSocialNetworkClass']
		    },

		    deliverableDetail (state, getters) {
		      return getters[this.namespace + 'deliverableDetail']
		    },
		    systemSettings (state, getters) {
		      return getters['systemSettings']
		    },
        getSystemSetting(state, getters) {
          return getters[this.namespace + 'getSystemSetting']
        },
	 	 }),


    apiUrl() {
      return process.env.MIX_API_BASE_URL;
    },
    /*
    getClasses() {
    	if (!this.isValid) {
    		return "error";
    	}
    },
    getClassesShortUrl() {
    	if (!this.isValidShortUrl) {
    		return "error";
    	}
    },
    */
    primaryPostClass: function () {
      let classNames = "";
      if (this.post) {
        const classNamesArray = this.post.social_networks.map(network => "platform-" + this.getSocialNetworkClassFromId(network));
        classNames += classNamesArray.join(' ');
        if (this.post.is_primary) {
          classNames += " primary-post";
        }
      }
      return classNames;
    },
    postCanBeSplit: function(){
      if(this.post.social_networks.length > 1 && this.post.is_primary){
        return true;
      }
      return false;
    },
    captionLength: function(){
      return this.caption.length;
    },
    shareUrlExists(){
      let exists = false;
      this.post.share_urls.forEach(function(share_url){
        if (share_url.url !== null){
          exists = true;
        }
      });
      return exists;
    },
    getFileClass() {

    	let txt = "post-images full-width-flex";
    	if (this.post) {
    		if (this.post.files.length > 0) {
    			txt = "post-images";
				}
			}
    	return txt;
    },
    getIdleLabel() {
    	let txt = "<i class='fas fa-upload'></i> Drag and drop files here.";
    	if (this.post) {
    		if (this.post.files.length > 0) {
    			txt = "<i class='fas fa-upload'></i>";
				}
			}
    	return txt;
    },
  },
  methods: {
  	...mapMutations({
  	  setDeliverableDetail (commit, payload) {
  	    return commit(this.namespace + 'setDeliverableDetail', payload)
  	  },
  	  setDeliverable (commit, payload) {
  	    return commit(this.namespace + 'setDeliverable', payload)
  	  },
  	}),

  	socialPostShareUrlUpdated(data) {
  		// update the deliverable details...
  		for (var xx =0; xx < this.post.share_urls.length; xx++) {
  			if (this.post.share_urls[xx].id == data.social_network_id) {
  				this.post.share_urls[xx].url = data.url;
  				this.post.share_urls[xx].only_share_url = data.only_share_url;
  			}
  		}

  		this.$nextTick(() => {
  			this.calcHasContent();
  		});
  	},

  	calcHasContent() {
  			var has_content = false;
  			var has_missing = false;
    		if (this.post) {
						if (this.post.caption != null && this.post.caption != "") {
							has_content = true;
						}
						if (!has_content) {
							// let's see if there is a re-tweet share url.
							if (this.post.share_urls) {
								if (this.post.share_urls.length > 0) {
									for (var xx =0; xx < this.post.share_urls.length; xx++) {
										var current_content = false;
										if (this.post.share_urls[xx].url != "" &&
										this.post.share_urls[xx].url != null
										)  {
											has_content = true;
											current_content = true;
										}
										if (!current_content) {
											has_missing = true;
										}
									}
								}
							}
						}
  			}
  			this.hasContent = !has_missing;
  	},

    // Resource upload file
    handleFilePondInit() {
      this.$refs.resourcePond._pond.setOptions({
        server: {
						headers: {
							Authorization: "Bearer " + this.$store.getters.authToken
						},
						process: {
							method: 'POST',
							url: `${this.apiUrl}uploadFileSocialMediaPost`,
							withCredentials: true,
              ondata: this.handleFilePondSubmit,
              onload: this.handleFilePondSuccess,
          },
        }
      });
    },
    handleFilePondSubmit(data) {
      data.append('id', this.post.id);
      return data;
    },
    handleFilePondSuccess(response) {
      let deliverableDetail = JSON.parse(response).deliverableDetail;
      this.setDeliverableDetail(deliverableDetail);
      this.setDeliverable(deliverableDetail);
    },
    postCaptionUpdated(e){
    	let that = this;
      this.disableButtonsInFieldset(`fs-url-${this.post.id}`);
    	var testNewValue = this.caption;
    	if (testNewValue == null) {
    		testNewValue = "";
    	}
    	testNewValue = testNewValue.trim();

    	var testOriginal = this.post.caption;
    	if (testOriginal == null) {
    		testOriginal = "";
    	}
      if(
        testNewValue != testOriginal &&
        !e.target.hasAttribute("disabled")
      ){
        e.target.setAttribute("disabled", "");
        this.$store.dispatch(this.namespace + "updateSocialMediaPostCaption", {
          id: this.post.id,
          value: testNewValue
        }).then( function(){
          e.target.removeAttribute("disabled");
          that.enableButtonsInFieldset(`fs-url-${that.post.id}`);
          that.$nextTick(() => {
          	that.calcHasContent();
          });
        });
      }
      this.$nextTick(() => {
      	this.calcHasContent();
      });
    },
    disableButtonsInFieldset(fieldsetId) {
      const fieldset = document.getElementById(fieldsetId);
      if (fieldset) {
        const buttons = fieldset.querySelectorAll('button');
        buttons.forEach(button => {
          button.setAttribute('disabled', true);
        });
      }
    },
    enableButtonsInFieldset(fieldsetId) {
      const fieldset = document.getElementById(fieldsetId);
      if (fieldset) {
        const buttons = fieldset.querySelectorAll('button');
        buttons.forEach(button => {
          button.removeAttribute('disabled');
        });
      }
    },
    shortenURL(postId) {
      let that = this;
      this.disableButtonsInFieldset(`fs-url-${this.post.id}`); 
      var testNewValue = this.caption;
      if (testNewValue == null) {
        testNewValue = "";
      }
      testNewValue = testNewValue.trim();
      const matches = Autolinker.parse(this.caption, {
        urls: true
      });
      this.isValid = true;
      if (matches.length > 0) {
        if (matches[0].type == "url") {
          testNewValue = matches[0].getUrl();
        } else {
          this.isValid = false;
        }
      } else {
        if (testNewValue == "") {
          this.isValid = true;
        } else {
          this.isValid = false;
        }
      }
      if (this.isValid) {
        const urlArray = matches
          .filter(match => match.type === 'url')
          .map(match => ({
            offset: match.offset,
            url: match.url
          }));
        const textareaId = 'post-caption-' + postId;
        const textarea = document.getElementById(textareaId);
        textarea.setAttribute('disabled', true);
        this.$store.dispatch(this.namespace + "getSocialMediaPostShorterUrl", {
          id: this.post.id,
          caption: this.post.caption,
          urlArray
        }).then(function () {
          that.enableButtonsInFieldset(`fs-url-${that.post.id}`);
          textarea.removeAttribute('disabled');
          that.initializePostEditor();
        });
      }
    },
    lengthenURL(postId) {
      let that = this;
      this.disableButtonsInFieldset(`fs-url-${this.post.id}`);
      var testNewValue = this.caption;
      if (testNewValue == null) {
        testNewValue = "";
      }
      testNewValue = testNewValue.trim();
      const matches = Autolinker.parse(this.caption, {
        urls: true
      });
      this.isValid = true;
      if (matches.length > 0) {
        if (matches[0].type == "url") {
          testNewValue = matches[0].getUrl();
        } else {
          this.isValid = false;
        }
      } else {
        if (testNewValue == "") {
          this.isValid = true;
        } else {
          this.isValid = false;
        }
      }
      if (this.isValid) {
        const urlArray = matches
          .filter(match => match.type === 'url')
          .map(match => ({
            offset: match.offset,
            url: match.url
          }));
        const textareaId = 'post-caption-' + postId;
        const textarea = document.getElementById(textareaId);
        textarea.setAttribute('disabled', true);
        this.$store.dispatch(this.namespace + "getSocialMediaPostLongerUrl", {
          id: this.post.id,
          caption: this.post.caption,
          urlArray
        }).then(function () {
          that.enableButtonsInFieldset(`fs-url-${that.post.id}`);
          textarea.removeAttribute('disabled');
          that.initializePostEditor();
        });
      }
    },
    /*
    postUrlUpdated(e){
      let that = this;
    	var testNewValue = this.url;
    	if (testNewValue == null) {
    		testNewValue = "";
    	}
    	testNewValue = testNewValue.trim();

      const matches = Autolinker.parse(this.url, {
          urls: true
      });
      this.isValid = true;
      if (matches.length > 0) {
      		if (matches[0].type == "url") {
      			testNewValue = matches[0].getUrl();
      		} else {
      			this.isValid = false;
      		}
      } else {
		    if (testNewValue == "") {
		    	   this.isValid = true;
		    } else {
		    	this.isValid = false;
		    }
      }
      if (this.isValid) {

	    	var testOriginal = this.post.url;
	    	if (testOriginal == null) {
	    		testOriginal = "";
	    	}
	    	if(
	    	  testNewValue != testOriginal &&
	    	  !e.target.hasAttribute("disabled")
	    	){
		        e.target.setAttribute("disabled", "");
		        this.$store.dispatch(this.namespace + "updateSocialMediaPostUrl", {
		          id: this.post.id,
		          value: testNewValue
		        }).then( function(){
		          e.target.removeAttribute("disabled");
	        		that.initializePostEditor();
		        });
	    	}
	    }
    },
    postShortUrlUpdated(e){
      let that = this;


    	var testNewValue = this.short_url;
    	if (testNewValue == null) {
    		testNewValue = "";
    	}
    	testNewValue = testNewValue.trim();

      const matches = Autolinker.parse(this.short_url, {
          urls: true
      });
      this.isValidShortUrl = true;
      if (matches.length > 0) {
      		if (matches[0].type == "url") {
      			testNewValue = matches[0].getUrl();
      		} else {
      			this.isValidShortUrl = false;
      		}
      } else {
		    if (testNewValue == "") {
		    	   this.isValidShortUrl = true;
		    } else {
		    	this.isValidShortUrl = false;
		    }
      }

      if (this.isValidShortUrl) {

	    	var testOriginal = this.post.short_url;
	    	if (testOriginal == null) {
	    		testOriginal = "";
	    	}
	    	if(
	    	  testNewValue != testOriginal &&
	    	  !e.target.hasAttribute("disabled")
	    	){
		        e.target.setAttribute("disabled", "");
		        this.$store.dispatch(this.namespace + "updateSocialMediaPostShortUrl", {
		          id: this.post.id,
		          value: this.short_url
		        }).then( function(){
		          e.target.removeAttribute("disabled");
	        		that.initializePostEditor();
		        });
	    	}
	    }
    },
    */

    imageOrderChanged(e){
      this.imageDraggingDisabled = true;
      let fileIds = [];
      this.post.files.forEach(function(file){
        fileIds.push(file.id);
      })
      let data = {
        id: this.post.id,
        file_id: fileIds
      }
      let that = this;
      this.$store.dispatch(this.namespace + "updateOrderFileSocialMediaPost", data).then(function(){
        that.imageDraggingDisabled = false;
      });
    },
    getSocialNetworkFromId(network_id){
      let network = this.systemSettings.social_networks.filter(network => network.id === network_id)[0];
      return network;
    },
    getSocialNetworkSlugFromId(network_id){
      let network = this.systemSettings.social_networks.filter(network => network.id === network_id)[0];
      return this.getSocialNetworkClass(network.api_type);
    },
    getSocialNetworkClassFromId(network_id){
      let network = this.systemSettings.social_networks.filter(network => network.id === network_id)[0];
      return this.getSocialNetworkClass(network.api_type);
    },

    getSocialNetworkNameFromId(network_id){
      let network = this.systemSettings.social_networks.filter(network => network.id === network_id)[0];
      return network.network_name;
    },
    networkButtonClick(e){
      if(this.updatingNetwork){
        return;
      }

      let button = e.currentTarget;
      if(!("networkId" in button.dataset)){
        return;
      }

      if(!this.post.is_primary){
        let id = parseInt(button.dataset.networkId);
        this.networkId = id;
        this.networkName = this.getSocialNetworkNameFromId(id);

        this.showNetworkReturn = true;
      } else if(!this.postCanBeSplit){
        this.showNetworkSplitErr = true;
      } else {
        let id = parseInt(button.dataset.networkId);
        
        this.networkId = id;
        this.networkName = this.getSocialNetworkNameFromId(id);
        
        this.showNetworkSplit = true;
      }
    },
    splitSocialNetwork(){
      this.updatingNetwork = true;
      let that = this;
      this.$store.dispatch(this.namespace + "splitSocialMediaNetworkPost", {
        id: this.post.id,
        social_network_id: this.networkId,
      }).then( function(){
        that.initializePostEditor();
      });
    },
    returnSocialNetwork(){
      this.updatingNetwork = true;
      let that = this;
      this.$store.dispatch(this.namespace + "returnSocialMediaPostNetwork", {
        id: this.post.id,
        social_network_id: this.networkId,
      }).then( function(){
        that.initializePostEditor();
      });
    },
    initializePostEditor(){
      if(this.post.caption == null){
        this.post.caption = "";
      }
      this.caption = this.post.caption;

      if(this.post.url  == null){
        this.post.url = "";
      }
      this.url = this.post.url;

      if(this.post.short_url == null){
        this.post.short_url = "";
      }
      this.short_url = this.post.short_url;
      this.post_images = this.post.files;

      this.networkId = false;
      this.networkName = "";
      this.updatingNetwork = false;
      if(this.shareUrlExists){
        this.showShareUrl = true;
      }

  		this.$nextTick(() => {
  			this.calcHasContent();
  		});
    },
    dragStart(){
      this.dragging=true;
    },
    dragUpdate(){
    },
    dragEnd(){
      this.dragging=false;
    },
    showShareUrlUpdated(value){
      if(!value && this.shareUrlExists){
        this.showShareUrlDisabled = true;
        let that = this;
        let urlsUpdated = 0;
        this.post.share_urls.forEach(function(share_url){
          that.$store.dispatch(that.namespace + "updateSocialPostNetworkShareUrl", {
            id: that.post.id,
            social_network_id: share_url.id,
            value: null,
          }).then(function(){
            urlsUpdated ++;
            if(urlsUpdated === that.post.share_urls.length){
              that.showShareUrlDisabled = false;
            }
            that.$nextTick(() => {
            	that.calcHasContent();
            });
          });
        });
      }
      this.calcHasContent();

    },
    insertEmoji(emoji){
      this.caption += emoji;
    },
    getRemainingWordCount(networkId){
      let network = this.getSocialNetworkFromId(networkId);
      return network.max_message_length - this.caption.length;
    },
    getRemainingWordClass(networkId){
      let network = this.getSocialNetworkFromId(networkId);
      let error = false;
      if(this.caption.length > network.max_message_length){
        error = true;
      }
      return {
        'error': error
      };
    },
  },
  created(){
    this.initializePostEditor();
  },
  mounted(){
    this.initializePostEditor();
  },
  
}
</script>
<style scoped>

.post-editor-fields-wrapper {
	padding: 0px 25px 25px 25px;
}

	.language-heading-wrapper {
	display:flex;
	padding:25px;
	}

	.language-heading {
		font-size:1.2rem;
		line-height: 1.7rem;
		margin-right: 5px;
	}
	.platforms {
		display: block;
		width:100%;

	}

	.platforms .remaining-words {
		display:block;
	}
.post-editor-language {
	width: 50%;
	max-width: 50%;
}
	.platform {
		min-width: auto;
		margin-left: 10px;
		margin-right: 10px;
		display:inline-block;
	}

.post-images.full-width-flex .filepond--wrapper {
	flex: 0 0 100% !important;
}

textarea.post-caption.min-height-set {
  min-height: 200px;
}

</style>